import { FC } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import RecurrenceFields from "./RecurrenceFields";
import useAxios from "../../hooks/useAxios";
import { API_ENDPOINTS } from "../../constants/api";
import { toaster } from "../../utils/toast";
import { utcToZonedString, zonedLocalToUtcString } from "../../utils/helper";
import { RecurrenceEndMode, RecurrenceFrequency, RecurrencePattern } from "../../utils/enums";
import { Campaign } from "../../redux/modules/auth/types";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";

export type EditRecurrenceModalProp = {
  show: boolean;
  campaign: Campaign | null;
  handleClose: () => void;
  onUpdated: () => void;
};

const EditRecurrenceModal: FC<EditRecurrenceModalProp> = ({ show, campaign, handleClose, onUpdated }) => {
  const { postData, isLoading } = useAxios();
  const { orgLocations, selectedLocationId } = useSelector((state: RootState) => state.user);
  const activeLocation = (orgLocations ?? []).find(
    (loc: any) => String(loc.id) === String(selectedLocationId)
  ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);
  const activeTimezone = activeLocation?.location_timezone || 'UTC';

  const validationSchema = Yup.object().shape({
    recurrenceFrequency: Yup.string().required("Frequency is required"),
    recurrenceDayOfWeek: Yup.string().test({
      test: function (value) {
        const needsDayOfWeek = this.parent.recurrenceFrequency === RecurrenceFrequency.WEEKLY
          || this.parent.recurrencePattern === RecurrencePattern.NTH_WEEKDAY;
        if (!needsDayOfWeek) return true;
        return (value !== "" && value !== undefined) || this.createError({ message: "Day of week is required" });
      }
    }),
    recurrencePattern: Yup.string().test({
      test: function (value) {
        if (this.parent.recurrenceFrequency !== RecurrenceFrequency.MONTHLY) return true;
        return !!value || this.createError({ message: "Monthly pattern is required" });
      }
    }),
    recurrenceDayOfMonth: Yup.string().test({
      test: function (value) {
        if (this.parent.recurrencePattern !== RecurrencePattern.DAY_OF_MONTH) return true;
        return !!value || this.createError({ message: "Day of month is required" });
      }
    }),
    recurrenceWeekOfMonth: Yup.string().test({
      test: function (value) {
        if (this.parent.recurrencePattern !== RecurrencePattern.NTH_WEEKDAY) return true;
        return !!value || this.createError({ message: "Occurrence is required" });
      }
    }),
    recurrenceTime: Yup.string().required("Send time is required"),
    recurrenceTimezone: Yup.string().required("Timezone is required"),
    recurrenceEndDate: Yup.string().test({
      test: function (value) {
        if (this.parent.recurrenceEndMode !== RecurrenceEndMode.END_DATE) return true;
        return !!value || this.createError({ message: "End date is required" });
      }
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      recurrenceFrequency: campaign?.recurrence_frequency ?? "",
      recurrencePattern: campaign?.recurrence_pattern ?? "",
      recurrenceDayOfWeek: campaign?.recurrence_day_of_week ?? "",
      recurrenceDayOfMonth: campaign?.recurrence_day_of_month ?? "",
      recurrenceWeekOfMonth: campaign?.recurrence_week_of_month ?? "",
      recurrenceTime: campaign?.recurrence_time ?? "09:00",
      recurrenceTimezone: campaign?.recurrence_timezone || activeTimezone,
      recurrenceEndMode: campaign?.recurrence_end_mode ?? RecurrenceEndMode.INDEFINITE,
      recurrenceEndDate: campaign?.recurrence_end_date ? utcToZonedString(campaign?.recurrence_end_date, campaign?.recurrence_timezone || activeTimezone) : "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!campaign) return;
      const targetTz = values.recurrenceTimezone || activeTimezone;
      const payload = {
        ...values,
        recurrenceDayOfWeek: values.recurrenceDayOfWeek === "" ? null : Number(values.recurrenceDayOfWeek),
        recurrenceDayOfMonth: values.recurrenceDayOfMonth === "" ? null : Number(values.recurrenceDayOfMonth),
        recurrenceEndDate: (values.recurrenceEndMode === RecurrenceEndMode.END_DATE && values.recurrenceEndDate)
          ? zonedLocalToUtcString(values.recurrenceEndDate, targetTz)
          : "",
      };
      const response = await postData(`${API_ENDPOINTS.UPDATE_RECURRENCE}/${campaign.id}/update-recurrence`, payload);
      const { success, message } = response;
      toaster({ message, success });
      if (success) {
        handleClose();
        onUpdated();
      }
    },
  });

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Form onSubmit={formik.handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>Edit Recurring Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <RecurrenceFields formik={formik} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" className="btn-theme btn-border" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" className="btn-theme" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditRecurrenceModal;
